import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Users, TrendingUp, Mail, Target, Palette, BarChart3, CheckCircle, Code, Bot } from 'lucide-react';
import { Button } from '../components/ui/button';

const services = [
  {
    id: 'social-media',
    icon: Users,
    title: 'Social Media Management',
    description: 'Strategic content creation and community management that builds authentic connections with your audience.',
    features: [
      'Platform-specific content strategy',
      'Community engagement & moderation',
      'Content calendar planning',
      'Audience growth tactics',
      'Performance analytics & reporting',
      'Trend monitoring & adaptation',
    ],
    platforms: ['TikTok', 'Instagram', 'Facebook', 'LinkedIn', 'Twitter/X', 'Lemon8'],
  },
  {
    id: 'affiliate-marketing',
    icon: TrendingUp,
    title: 'Affiliate Marketing',
    description: 'Scale your brand through strategic creator partnerships and optimized affiliate programs.',
    features: [
      'Creator vetting & management',
      'Program setup & optimization',
      'Performance tracking',
      'Content coordination',
      'ROI analysis & reporting',
      'Partnership negotiations',
    ],
    platforms: ['TikTok Shop', 'Insense', 'Creator Networks'],
  },
  {
    id: 'email-marketing',
    icon: Mail,
    title: 'Email Marketing',
    description: 'Engaging campaigns that nurture leads, build relationships, and drive conversions.',
    features: [
      'Campaign strategy & planning',
      'Email design & copywriting',
      'List segmentation',
      'A/B testing & optimization',
      'Automation workflows',
      'Performance analytics',
    ],
    platforms: ['Mailchimp', 'Klaviyo', 'Kajabi', 'ConvertKit'],
  },
  {
    id: 'ads-management',
    icon: Target,
    title: 'Ads Management',
    description: 'Data-driven advertising that maximizes ROAS and minimizes wasted spend.',
    features: [
      'Campaign strategy & setup',
      'Audience targeting & research',
      'Creative development',
      'Budget optimization',
      'A/B testing',
      'Performance reporting & insights',
    ],
    platforms: ['Facebook Ads', 'Instagram Ads', 'TikTok Ads', 'Google Ads'],
  },
  {
    id: 'content-strategy',
    icon: Palette,
    title: 'Content Strategy',
    description: 'Compelling content that resonates with your audience and establishes brand authority.',
    features: [
      'Brand voice development',
      'Content pillar planning',
      'UGC content creation',
      'Video production',
      'Graphic design',
      'Copywriting & storytelling',
    ],
    platforms: ['Canva', 'CapCut', 'Adobe Suite'],
  },
  {
    id: 'analytics',
    icon: BarChart3,
    title: 'Analytics & Reporting',
    description: 'Comprehensive KPI tracking and actionable insights to inform strategic decisions.',
    features: [
      'KPI definition & tracking',
      'Custom dashboard creation',
      'Monthly performance reports',
      'Competitive analysis',
      'Market research',
      'Strategic recommendations',
    ],
    platforms: ['Google Analytics', 'Meta Business Suite', 'Platform Insights'],
  },
  {
    id: 'web-development',
    icon: Code,
    title: 'Web Development',
    description: 'Custom websites and web applications built with modern technologies and optimized for performance.',
    features: [
      'Custom website development',
      'E-commerce solutions',
      'Landing page design',
      'Performance optimization',
      'SEO implementation',
      'Maintenance & support',
    ],
    platforms: ['React', 'Next.js', 'WordPress', 'Shopify'],
  },
  {
    id: 'ai-engineering',
    icon: Bot,
    title: 'AI Product Engineering',
    description: 'Agentic workflows and intelligent automation with high-fidelity UX design for seamless user experiences.',
    features: [
      'Agentic workflow design',
      'AI integration & automation',
      'High-fidelity UX design',
      'Custom AI solutions',
      'Process optimization',
      'Technical consulting',
    ],
    platforms: ['OpenAI', 'Custom LLMs', 'Automation Tools'],
  },
];

export const ServicesPage = () => {
  return (
    <div className="min-h-screen pt-20" data-testid="services-page">
      {/* Hero Section */}
      <section className="section-padding bg-forest" data-testid="services-hero">
        <div className="container-custom">
          <motion.div
            className="max-w-4xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-gold font-medium tracking-widest uppercase text-sm mb-6 block">
              Our Services
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-cream leading-[1.1] mb-6">
              Comprehensive Digital{' '}
              <span className="text-gold">Marketing Solutions</span>
            </h1>
            <p className="text-xl text-cream/80 max-w-2xl leading-relaxed">
              From strategy to execution, we provide end-to-end digital marketing services 
              tailored to your brand's unique needs and goals.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services List */}
      <section className="section-padding bg-cream" data-testid="services-list">
        <div className="container-custom">
          <div className="space-y-24">
            {services.map((service, index) => (
              <motion.div
                key={service.id}
                id={service.id}
                className={`grid lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                data-testid={`service-section-${service.id}`}
              >
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="w-16 h-16 bg-gold rounded-2xl flex items-center justify-center mb-6">
                    <service.icon className="text-forest-dark" size={32} />
                  </div>
                  <h2 className="text-3xl md:text-4xl font-bold text-forest mb-4">
                    {service.title}
                  </h2>
                  <p className="text-lg text-forest/70 mb-8 leading-relaxed">
                    {service.description}
                  </p>
                  
                  <div className="space-y-3 mb-8">
                    {service.features.map((feature, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <CheckCircle className="text-gold flex-shrink-0" size={20} />
                        <span className="text-forest/80">{feature}</span>
                      </div>
                    ))}
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {service.platforms.map((platform) => (
                      <span
                        key={platform}
                        className="bg-forest/10 text-forest px-4 py-2 rounded-full text-sm font-medium"
                      >
                        {platform}
                      </span>
                    ))}
                  </div>
                </div>

                <div className={`${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                  <div className="bg-forest-dark rounded-2xl p-8 lg:p-12">
                    <div className="aspect-square flex items-center justify-center">
                      <service.icon className="text-gold w-32 h-32 opacity-20" />
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="section-padding bg-forest-dark" data-testid="services-process">
        <div className="container-custom">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-gold font-medium tracking-widest uppercase text-sm mb-4 block">
              Our Process
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-cream">
              How We Work
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Discovery', description: 'We learn about your brand, goals, and target audience.' },
              { step: '02', title: 'Strategy', description: 'We develop a custom plan backed by data and research.' },
              { step: '03', title: 'Execution', description: 'We implement the strategy with creative excellence.' },
              { step: '04', title: 'Optimize', description: 'We track KPIs and continuously improve performance.' },
            ].map((item, index) => (
              <motion.div
                key={item.step}
                className="text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`process-step-${index}`}
              >
                <span className="text-6xl font-bold text-gold/20 block mb-4">{item.step}</span>
                <h3 className="text-xl font-semibold text-cream mb-3">{item.title}</h3>
                <p className="text-cream/60">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gold" data-testid="services-cta">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-forest-dark mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-xl text-forest-dark/70 mb-10 max-w-2xl mx-auto">
              Let's discuss how we can help you achieve your digital marketing goals.
            </p>
            <Link to="/contact" data-testid="services-cta-button">
              <Button className="bg-forest text-cream hover:bg-forest-dark rounded-full px-10 py-6 text-lg font-semibold">
                Schedule a Consultation
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
